/**
 * Subscription data + mutation layer.
 *
 * `GET subscription/plans` is PUBLIC (cacheable for anonymous viewers on the
 * `/pricing.html` page) — its response carries the plan list AND the
 * CLIENT-SIDE gateway credentials (PayPal `client_id`, Cashfree `mode`). The
 * back-end signs each plan with a per-plan `token` that the create/verify
 * endpoints expect inside the URL.
 *
 * The create/verify/cancel mutations are AUTH-gated — the typed `api` client
 * attaches the bearer token automatically.
 */
import { useMutation, useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api/client';
import { queryKeys } from '@/lib/api/queryKeys';
import type {
  SubscriptionCreateResult,
  SubscriptionPlansResult,
  SubscriptionVerifyResult,
} from '@/types/api';

/** Parameters accepted by `useSubscriptionPlans` (currently just `cplan`). */
export interface SubscriptionPlansParams {
  /** Current plan code (e.g. `'free'`) — annotates the response's `active` flag. */
  cplan?: string;
}

function toQuery(params: Record<string, unknown> = {}): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

/**
 * Public subscription plans (`GET subscription/plans`). Cached for 2 minutes —
 * matches the legacy API's `cache 120` annotation. Carries gateway credentials
 * inline (the modal reads them straight off the result).
 */
export function useSubscriptionPlans(params: SubscriptionPlansParams = {}) {
  const query = { cplan: params.cplan };
  return useQuery({
    queryKey: queryKeys.subscription.plans(query),
    staleTime: 2 * 60_000,
    // Plan + gateway list rarely changes; consulted from sidebar
    // card + dialog + pricing page — keep in cache for 30 min so
    // toggling between those surfaces doesn't refetch.
    gcTime: 30 * 60_000,
    queryFn: () =>
      api.get<SubscriptionPlansResult>(`subscription/plans${toQuery(query)}`),
  });
}

/** Input for `useCreateSubscription` — the per-plan id + signed token. */
export interface CreateSubscriptionInput {
  planId: number;
  token: string;
  /** Gateway code, e.g. `'paypal'` / `'cashfree'`. */
  gateway: string;
  /** Currency from the gateway credentials block. */
  currency: string;
  /** Free-form per-gateway params (period, phone, etc). */
  params?: Record<string, unknown>;
  /** Per-gateway extra info passed through to the backend. */
  pinfo?: Record<string, unknown>;
}

/**
 * `POST subscription/create/:plan_id/:token` — kicks off a subscription with
 * the chosen gateway. For PayPal the result's `additional` is the
 * `actions.subscription.create(args)` argument; for Cashfree it's the session
 * payload `cashfree.checkout(args)` consumes.
 */
export function useCreateSubscription() {
  return useMutation({
    mutationFn: ({
      planId,
      token,
      gateway,
      currency,
      params,
      pinfo,
    }: CreateSubscriptionInput) =>
      api.post<SubscriptionCreateResult>(
        `subscription/create/${planId}/${token}`,
        { gateway, currency, params: params ?? {}, pinfo: pinfo ?? {} },
      ),
  });
}

/** Input for `useVerifySubscription`. */
export interface VerifySubscriptionInput {
  planId: number;
  token: string;
  /** Provider transaction id (returned by `create`). */
  transactionId: number | string;
  /** Gateway code. */
  gateway: string;
  currency: string;
  /** Provider-supplied confirmation payload (`subscriptionID` for PayPal). */
  pinfo?: Record<string, unknown>;
}

/**
 * `POST subscription/verify/:plan_id/:token?trans_id=...` — confirms the gateway
 * callback and (on success) returns the activated `subscription` block + plan.
 */
export function useVerifySubscription() {
  return useMutation({
    mutationFn: ({
      planId,
      token,
      transactionId,
      gateway,
      currency,
      pinfo,
    }: VerifySubscriptionInput) =>
      api.post<SubscriptionVerifyResult>(
        `subscription/verify/${planId}/${token}?trans_id=${transactionId}`,
        { gateway, currency, params: {}, pinfo: pinfo ?? {} },
      ),
  });
}

/** `PUT subscription/cancel/:id` — cancel an active subscription. */
export function useCancelSubscription() {
  return useMutation({
    mutationFn: (subscriptionId: string | number) =>
      api.put<SubscriptionVerifyResult>(`subscription/cancel/${subscriptionId}`),
  });
}
